import React, { useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  ArrowSquareOut,
  CalendarBlank,
  Check,
  CheckCircle,
  Compass,
  FileText,
  FlagCheckered,
  Plus,
  Sparkle,
  TreeStructure,
  Warning,
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { IngestIssuesTable } from "./IngestIssuesTable";
import { hasText } from "./methodologyMode";
import useEsgAdminFeaturesVisible from "hooks/useEsgAdminFeaturesVisible";
import RichTextEditor from "../../ISSB/IssbDataInput/components/RichTextEditor/RichTextEditor";

// The Commit stage's right-hand rail — the brief's "Commit rail". Everything it needs comes in as
// `commitState` (a `useCommitState()` result, built once by CommitStage) and `onOpenTask`/
// `onStartNewSession` — this component itself holds no data-fetching hooks, only the local UI state for
// things that are purely this rail's own (scroll divider, Start-a-new-session spinner).
//
// Layout: a column flex with two children — a `flex="1" minH={0} overflowY="auto"` scroll region (task
// card, decision, methodology) and a `flexShrink={0}` footer bar, so the Commit CTA never scrolls out of
// view. This only works because CommitStage's ancestor chain already provides a real height.

const SCROLL_SX = {
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
  "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",
  "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
};

export const CommitPanel = ({ commitState, onOpenTask, onStartNewSession, width = "420px" }) => {
  const {
    targetLabel,
    scopePill,
    period,
    dimensions,
    hasExisting,
    existingRowCount,
    isLoadingExistingCount,
    mappedCount,
    isBuilding,
    payload,
    payloadError,
    issues,
    notCommittable,
    mode,
    setMode,
    isReplace,
    replaceAcknowledged,
    setReplaceAcknowledged,
    validated,
    isDryRunning,
    ingestError,
    methodology,
    setMethodology,
    existingMethodology,
    methodologySaveError,
    isGeneratingMethodology,
    generateError,
    handleGenerateDraft,
    canCommit,
    isCommitting,
    isConfirmOpen,
    setConfirmOpen,
    handleConfirmYes,
    committed,
    replaceBlocked,
    rowsAfter,
  } = commitState;

  const [showTrail, setShowTrail] = useState(false);
  // The raw session trail is internal detail — ESG-Admin only, and honours the header's hide toggle.
  const showAdminFeatures = useEsgAdminFeaturesVisible();
  const [isStartingNewSession, setStartingNewSession] = useState(false);

  const handleStartNewSession = async () => {
    setStartingNewSession(true);
    try {
      await onStartNewSession?.();
    } finally {
      setStartingNewSession(false);
    }
  };

  if (committed) {
    return (
      <Flex direction="column" h="100%" minH={0} w={width} flexShrink={0} borderLeft="1px solid" borderColor="gray.200" bg="#FBFCFD">
        <Box flex="1" minH={0} overflowY="auto" px={4} py={5} sx={SCROLL_SX}>
          <CommittedState
            committed={committed}
            mode={mode}
            existingRowCount={existingRowCount ?? 0}
            mappedCount={mappedCount}
            targetLabel={targetLabel}
            period={period}
            methodologyIncluded={methodology.trim().length > 0}
            methodologySaveError={methodologySaveError}
            onOpenTask={onOpenTask}
            onStartNewSession={handleStartNewSession}
            isStartingNewSession={isStartingNewSession}
          />
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="100%" minH={0} w={width} flexShrink={0} borderLeft="1px solid" borderColor="gray.200" bg="#FBFCFD">
      <Box flex="1" minH={0} overflowY="auto" px={4} py={5} sx={SCROLL_SX}>
        <Stack spacing={4}>
          <Text fontSize="18px" fontWeight="800" color="darkTeal">
            Commit to the data task
          </Text>

          <TaskCard
            targetLabel={targetLabel}
            scopePill={scopePill}
            period={period}
            dimensions={dimensions}
            hasExisting={hasExisting}
            existingRowCount={existingRowCount}
            isLoadingExistingRowCount={isLoadingExistingCount}
            mappedCount={mappedCount}
            isLoadingMapped={isBuilding}
          />

          {payloadError && (
            <Alert status="error" borderRadius="md" fontSize="13px">
              <AlertIcon />
              {payloadError}
            </Alert>
          )}

          {isBuilding && !payload && (
            <HStack color="gray.500" fontSize="13px">
              <Spinner size="sm" />
              <Text>Preparing the rows…</Text>
            </HStack>
          )}

          {notCommittable && (
            <Stack spacing={2}>
              <Alert status="warning" borderRadius="md" fontSize="13px" alignItems="start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="600">This output can&apos;t be committed yet</Text>
                  <Text color="gray.600">
                    Ask the AI to fix the points below — naming each output column after the task field it
                    fills is usually all it takes.
                  </Text>
                </Box>
              </Alert>
              <IngestIssuesTable issues={issues} droppedCount={payload.dropped_issue_count} />
            </Stack>
          )}

          {payload?.committable && hasExisting && (
            <DecisionCards mode={mode} setMode={setMode} existingRowCount={existingRowCount} mappedCount={mappedCount} />
          )}

          {payload?.committable && isReplace && (
            <ReplaceWarning
              existingRowCount={existingRowCount}
              mappedCount={mappedCount}
              replaceAcknowledged={replaceAcknowledged}
              setReplaceAcknowledged={setReplaceAcknowledged}
              hasValidated={Boolean(validated)}
            />
          )}

          {payload?.committable && (
            <>
              <DroppedColumnsWarning ignoredColumns={payload.ignored_columns} />

              <MethodologySection
                html={methodology}
                onChange={setMethodology}
                existingHtml={existingMethodology}
                isReplace={isReplace}
                hasExistingRows={hasExisting}
                onGenerateDraft={handleGenerateDraft}
                isGenerating={isGeneratingMethodology}
                generateError={generateError}
              />

              {showAdminFeatures ? (
              <TrailPreview
                markdown={payload.trail_markdown}
                isOpen={showTrail}
                onToggle={() => setShowTrail((v) => !v)}
              />
              ) : null}
            </>
          )}

          {ingestError && (
            <Stack spacing={2}>
              <Alert status="error" borderRadius="md" fontSize="13px" alignItems="start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="600">{ingestError.title}</Text>
                  <Text color="gray.700">{ingestError.description}</Text>
                </Box>
              </Alert>
              {ingestError.isValidationFailure && (
                <IngestIssuesTable
                  issues={ingestError.rowErrors}
                  droppedCount={Math.max(0, ingestError.totalRowErrors - ingestError.rowErrors.length)}
                />
              )}
            </Stack>
          )}

        </Stack>
      </Box>

      {payload?.committable && (
        <CommitFooterBar
          isReplace={isReplace}
          existingRowCount={existingRowCount ?? 0}
          mappedCount={mappedCount}
          rowsAfter={rowsAfter}
          onCommitClick={() => setConfirmOpen(true)}
          canCommit={canCommit && (!isReplace || replaceAcknowledged)}
          isDryRunning={isDryRunning}
        />
      )}

      <CommitConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setConfirmOpen(false)}
        onYes={handleConfirmYes}
        isSubmitting={isDryRunning || isCommitting}
        mode={mode}
        targetLabel={targetLabel}
        period={period}
        existingRowCount={existingRowCount ?? 0}
        mappedCount={mappedCount}
        methodologyIncluded={methodology.trim().length > 0}
      />
    </Flex>
  );
};

const rowsText = (count, isLoading) => {
  if (isLoading) return "…";
  if (count == null) return "—";
  return count.toLocaleString();
};

/**
 * The platform's standard task card, per the brief: a grey header band holding the scope chip only (no
 * assignee, no title), then the metadata row (period + every pinned dimension) and the numbers row.
 */
const TaskCard = ({
  targetLabel,
  scopePill,
  period,
  dimensions,
  hasExisting,
  existingRowCount,
  isLoadingExistingRowCount,
  mappedCount,
  isLoadingMapped,
}) => {
  const ScopeIcon = scopePill?.icon;
  const hasMeta = Boolean(period) || dimensions.length > 0;

  return (
    <Box bg="pureWhite" border="1px solid" borderColor="gray.200" borderRadius="12px" overflow="hidden">
      {targetLabel && (
        <Box bg="#F8FAFC" borderBottom="1px solid" borderColor="gray.200" px={4} py={3}>
          <Box
            display="inline-flex"
            alignItems="center"
            gap="4px"
            bg={scopePill?.bg || "lightLime"}
            color={scopePill?.color || "limeShade.700"}
            fontSize="13px"
            fontWeight="600"
            px="10px"
            h="24px"
            borderRadius="full"
            whiteSpace="nowrap"
          >
            {ScopeIcon && <ScopeIcon size={13} weight="fill" />}
            <Text as="span">{targetLabel}</Text>
          </Box>
        </Box>
      )}

      <Box p={4}>
        {hasMeta && (
          <HStack spacing={4} flexWrap="wrap" color="gray.600">
            {period && (
              <HStack spacing={1.5}>
                <CalendarBlank size={14} color="#A0AEC0" />
                <Text fontSize="13px">{period}</Text>
              </HStack>
            )}
            {dimensions.map((dim) => (
              <Tooltip key={dim.level} label={dim.level} openDelay={400}>
                <HStack spacing={1.5}>
                  <TreeStructure size={14} color="#A0AEC0" />
                  <Text fontSize="13px">{dim.value}</Text>
                </HStack>
              </Tooltip>
            ))}
          </HStack>
        )}

        <HStack spacing={8} pt={3} mt={3} borderTop="1px solid" borderColor="gray.200" flexWrap="wrap">
          {hasExisting && (
            <Box>
              <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">
                Existing data
              </Text>
              <HStack align="baseline" spacing={1}>
                <Text fontSize="22px" fontWeight="800" color="gray.800">
                  {rowsText(existingRowCount, isLoadingExistingRowCount)}
                </Text>
                <Text fontSize="13px" color="gray.500">
                  rows
                </Text>
              </HStack>
            </Box>
          )}
          <Box>
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">
              Ready to commit
            </Text>
            <HStack align="baseline" spacing={1}>
              <Text fontSize="22px" fontWeight="800" color="darkTeal">
                {rowsText(mappedCount, isLoadingMapped)}
              </Text>
              <Text fontSize="13px" color="gray.500">
                rows
              </Text>
            </HStack>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

/**
 * "How should these rows be written?" — two large RadioGroup cards. Selected Add = lime border/ring;
 * selected Replace = orange (destructive) border/ring. Only rendered when the destination already has
 * rows — with none, there is no decision to make.
 */
const DecisionCards = ({ mode, setMode, existingRowCount, mappedCount }) => (
  <Box>
    <Text fontSize="12px" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase" mb={2}>
      How should these rows be written?
    </Text>
    <RadioGroup value={mode} onChange={setMode}>
      <Stack spacing={3}>
        <Box
          as="label"
          border="2px solid"
          borderColor={mode === "APPEND" ? "lime" : "gray.200"}
          boxShadow={mode === "APPEND" ? "0 0 0 3px var(--chakra-colors-lightLime)" : "none"}
          borderRadius="12px"
          p={4}
          cursor="pointer"
          transition="all 0.12s"
        >
          <HStack align="start" spacing={3}>
            <Radio value="APPEND" colorScheme="limeShade" mt="2px" />
            <Box>
              <Text fontWeight="700" color="darkTeal">
                Add to existing
              </Text>
              <Text fontSize="13px" color="gray.600" mt={1}>
                Keep the {existingRowCount.toLocaleString()} rows already in the task and write{" "}
                {mappedCount.toLocaleString()} more. The methodology is added to.
              </Text>
            </Box>
          </HStack>
        </Box>
        <Box
          as="label"
          border="2px solid"
          borderColor={mode === "REPLACE_TARGET" ? "destructive.text" : "gray.200"}
          boxShadow={mode === "REPLACE_TARGET" ? "0 0 0 3px var(--chakra-colors-destructive-bg)" : "none"}
          borderRadius="12px"
          p={4}
          cursor="pointer"
          transition="all 0.12s"
        >
          <HStack align="start" spacing={3}>
            <Radio value="REPLACE_TARGET" colorScheme="orange" mt="2px" />
            <Box>
              <Text fontWeight="700" color="darkTeal">
                Replace all rows
              </Text>
              <Text fontSize="13px" color="gray.600" mt={1}>
                Delete the {existingRowCount.toLocaleString()} existing rows and write{" "}
                {mappedCount.toLocaleString()} in their place. The methodology is replaced too.
              </Text>
            </Box>
          </HStack>
        </Box>
      </Stack>
    </RadioGroup>
  </Box>
);

/**
 * The destructive-mode warning + acknowledgement, shown inline only while Replace is selected.
 */
const ReplaceWarning = ({ existingRowCount, mappedCount, replaceAcknowledged, setReplaceAcknowledged, hasValidated }) => (
  <Box bg="destructive.bg" border="1px solid" borderColor="destructive.border" borderRadius="12px" p={4}>
    <HStack spacing={2} mb={2}>
      <Warning size={16} weight="fill" color="#C4620A" />
      <Text fontSize="13px" color="destructive.textDark">
        <Text as="span" fontWeight="700">
          All {existingRowCount.toLocaleString()} existing rows will be permanently deleted
        </Text>{" "}
        and replaced with the {mappedCount.toLocaleString()} from this session. This cannot be undone.
      </Text>
    </HStack>
    <Checkbox colorScheme="orange" isChecked={replaceAcknowledged} onChange={(e) => setReplaceAcknowledged(e.target.checked)}>
      <Text fontSize="13px" color="destructive.textDark">
        I understand the existing rows will be removed
      </Text>
    </Checkbox>
    {!hasValidated && (
      <Text fontSize="12px" color="destructive.textDark" mt={2}>
        Checking against the task — this reports how many rows would be deleted, and refuses if that
        number has moved since.
      </Text>
    )}
  </Box>
);

/**
 * The pinned footer: a big "rows in task after commit" number, a Kept/Added (or Deleted/Written) recap,
 * and the full-width Commit button. Fixed depth (`flexShrink: 0`) so it never scrolls out of view.
 */
const CommitFooterBar = ({ isReplace, existingRowCount, mappedCount, rowsAfter, onCommitClick, canCommit, isDryRunning }) => (
  <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200" bg="pureWhite" px={4} py={4}>
    <Flex justify="space-between" align="flex-end" mb={3} flexWrap="wrap" gap={3}>
      <HStack align="baseline" spacing={2}>
        <Text fontSize="26px" fontWeight="800" color="darkTeal">
          {rowsAfter.toLocaleString()}
        </Text>
        <Text fontSize="13px" color="gray.500">
          rows in task after commit
        </Text>
      </HStack>
      <Stack spacing={0} textAlign="right">
        <Text fontSize="13px" color="gray.600">
          {isReplace ? "Deleted" : "Kept"} {existingRowCount.toLocaleString()}
        </Text>
        <Text fontSize="13px" fontWeight="700" color={isReplace ? "destructive.text" : "lime"}>
          {isReplace ? "Written" : "Added"} {mappedCount.toLocaleString()}
        </Text>
      </Stack>
    </Flex>
    <Button
      w="100%"
      size="lg"
      bg={isReplace ? "destructive.text" : "darkTeal"}
      color="white"
      _hover={{ bg: isReplace ? "destructive.textDark" : "teal" }}
      leftIcon={<FlagCheckered size={18} weight="bold" />}
      onClick={onCommitClick}
      isLoading={isDryRunning}
      loadingText="Checking…"
      isDisabled={!canCommit}
    >
      {isReplace
        ? `Replace ${existingRowCount.toLocaleString()} with ${mappedCount.toLocaleString()} rows`
        : `Commit ${mappedCount.toLocaleString()} rows`}
    </Button>
  </Box>
);

/**
 * Yes/No confirmation before anything is written. Only Yes triggers the commit.
 */
const CommitConfirmModal = ({
  isOpen,
  onClose,
  onYes,
  isSubmitting,
  mode,
  targetLabel,
  period,
  existingRowCount,
  mappedCount,
  methodologyIncluded,
}) => {
  const isReplace = mode === "REPLACE_TARGET";
  const rowsAfter = isReplace ? mappedCount : existingRowCount + mappedCount;
  const destinationText = period ? `${targetLabel} · ${period}` : targetLabel;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="rgba(5,31,35,0.48)" />
      <ModalContent maxW="480px" borderRadius="14px">
        <ModalBody p={6}>
          <HStack align="start" spacing={3} mb={2}>
            <Flex w="40px" h="40px" flexShrink={0} borderRadius="10px" align="center" justify="center" bg={isReplace ? "destructive.text" : "darkTeal"}>
              {isReplace ? <Warning size={20} weight="fill" color="white" /> : <FlagCheckered size={20} weight="fill" color="white" />}
            </Flex>
            <Box>
              <Text fontSize="18px" fontWeight="800" color="darkTeal">
                {isReplace
                  ? `Replace ${existingRowCount.toLocaleString()} rows with ${mappedCount.toLocaleString()}?`
                  : `Commit ${mappedCount.toLocaleString()} rows to the task?`}
              </Text>
              <Text fontSize="13px" color="gray.600" mt={1}>
                {isReplace ? (
                  <>
                    <Text as="span" fontWeight="700" color="destructive.text">
                      All {existingRowCount.toLocaleString()} existing rows will be permanently deleted
                    </Text>{" "}
                    from <Text as="span" fontWeight="600">{destinationText}</Text>. This cannot be undone.
                  </>
                ) : (
                  <>
                    These rows will be written to <Text as="span" fontWeight="600">{destinationText}</Text>.
                    {existingRowCount > 0 && " Existing data is kept."}
                  </>
                )}
              </Text>
            </Box>
          </HStack>

          <Stack spacing={0} border="1px solid" borderColor="gray.200" borderRadius="10px" mt={4} overflow="hidden">
            {isReplace ? (
              <>
                <ConfirmRow label="Deleted" value={`${existingRowCount.toLocaleString()} rows`} color="destructive.text" />
                <ConfirmRow label="Written" value={`${mappedCount.toLocaleString()} rows`} />
              </>
            ) : (
              <>
                <ConfirmRow label="Kept" value={`${existingRowCount.toLocaleString()} rows`} />
                <ConfirmRow label="Added" value={`${mappedCount.toLocaleString()} rows`} />
              </>
            )}
            <ConfirmRow label="Rows in task after" value={rowsAfter.toLocaleString()} />
            <ConfirmRow label="Methodology" value={methodologyIncluded ? "Included" : "None"} isLast />
          </Stack>
        </ModalBody>
        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200" borderBottomRadius="14px">
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          {isReplace ? (
            <Button bg="destructive.text" color="white" _hover={{ bg: "destructive.textDark" }} onClick={onYes} isLoading={isSubmitting} loadingText="Replacing…">
              Yes, replace
            </Button>
          ) : (
            <Button variant="darkTeal" onClick={onYes} isLoading={isSubmitting} loadingText="Committing…">
              Yes, commit
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ConfirmRow = ({ label, value, color = "darkTeal", isLast }) => (
  <Flex justify="space-between" px={4} py={3} borderBottom={isLast ? "none" : "1px solid"} borderColor="gray.200">
    <Text fontSize="13px" color="gray.600">
      {label}
    </Text>
    <Text fontSize="13px" fontWeight="700" color={color}>
      {value}
    </Text>
  </Flex>
);

const OutcomeStat = ({ label, value, color = "darkTeal", bg, dividerColor = "gray.200" }) => (
  <Box flex="1" p={4} bg={bg} borderRight="1px solid" borderColor={dividerColor} _last={{ borderRight: "none" }}>
    <Text fontSize="13px" color="gray.600">
      {label}
    </Text>
    <Text fontSize="28px" fontWeight="800" color={color} mt={1}>
      {value.toLocaleString()}
    </Text>
  </Box>
);

/**
 * The rail after a successful commit — a summary plus "Open task" / "Start a new session".
 */
const CommittedState = ({
  committed,
  mode,
  existingRowCount,
  mappedCount,
  targetLabel,
  period,
  methodologyIncluded,
  methodologySaveError,
  onOpenTask,
  onStartNewSession,
  isStartingNewSession,
}) => {
  const isReplace = mode === "REPLACE_TARGET";
  const rowsWritten = committed.rowsAccepted ?? mappedCount;
  const rowsAfter = isReplace ? rowsWritten : existingRowCount + rowsWritten;
  const destinationText = period ? `${targetLabel} · ${period}` : targetLabel;

  return (
    <Box bg="successCard.bg" border="1px solid" borderColor="successCard.border" borderRadius="14px" p={5}>
      <HStack align="start" spacing={3}>
        <Flex w="40px" h="40px" flexShrink={0} borderRadius="10px" bg="lime" align="center" justify="center">
          <Check size={20} weight="bold" color="#051F23" />
        </Flex>
        <Box flex="1">
          <Text fontSize="18px" fontWeight="800" color="darkTeal">
            {rowsWritten.toLocaleString()} rows committed
          </Text>
          <Text fontSize="13px" color="gray.600" mt={1}>
            Written to <Text as="span" fontWeight="700">{destinationText}</Text> just now. The task now
            holds <Text as="span" fontWeight="700">{rowsAfter.toLocaleString()}</Text> rows.
          </Text>

          <Flex border="1px solid" borderColor="successCard.border" borderRadius="10px" bg="white" mt={4} overflow="hidden">
            {isReplace ? (
              <>
                <OutcomeStat label="Deleted" value={existingRowCount} color="destructive.text" bg="white" />
                <OutcomeStat label="Written" value={rowsWritten} color="darkTeal" bg="white" />
              </>
            ) : (
              <>
                <OutcomeStat label="Kept" value={existingRowCount} color="darkTeal" bg="white" />
                <OutcomeStat label="Added" value={rowsWritten} color="darkTeal" bg="white" />
              </>
            )}
            <OutcomeStat label="Rows in task now" value={rowsAfter} color="darkTeal" bg="white" />
          </Flex>

          <Stack spacing={1} mt={4}>
            {methodologyIncluded && (
              <HStack spacing={2}>
                <CheckCircle size={16} weight="fill" color="#63AD03" />
                <Text fontSize="13px" color="gray.700">
                  Methodology saved to the task
                </Text>
              </HStack>
            )}
            <HStack spacing={2}>
              <CheckCircle size={16} weight="fill" color="#63AD03" />
              <Text fontSize="13px" color="gray.700">
                Session trail stored with this commit
              </Text>
            </HStack>
          </Stack>
          {methodologySaveError ? (
            <Alert status="warning" borderRadius="md" mt={3} fontSize="12px">
              <AlertIcon />
              {methodologySaveError}
            </Alert>
          ) : null}

          <HStack spacing={3} mt={4} flexWrap="wrap">
            <Button variant="darkTeal" leftIcon={<ArrowSquareOut size={16} weight="bold" />} onClick={onOpenTask}>
              Open data task
            </Button>
            <Button
              variant="outline"
              leftIcon={<Plus size={16} weight="bold" />}
              onClick={onStartNewSession}
              isLoading={isStartingNewSession}
              loadingText="Starting…"
            >
              Start a new session
            </Button>
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
};

/**
 * Columns the task has no field for, which are being dropped.
 */
const DroppedColumnsWarning = ({ ignoredColumns }) => {
  if (!ignoredColumns?.length) return null;
  return (
    <Text fontSize="12px" color="gray.500">
      Not sent: {ignoredColumns.join(", ")}. Columns the task has no field for are left behind rather than
      guessed at.
    </Text>
  );
};

const MethodologySection = ({
  html,
  onChange,
  existingHtml,
  isReplace,
  hasExistingRows,
  onGenerateDraft,
  isGenerating,
  generateError,
}) => {
  // The editor is the task's methodology AFTER the commit: it follows the rows' Add/Replace choice above.
  // Add starts it with the previous methodology and Generate appends below that; Replace starts it empty,
  // because the previous methodology is discarded with the rows it described.
  const hasExisting = hasText(existingHtml);
  const note = isReplace
    ? hasExisting
      ? "Replacing all rows discards the previous methodology — this starts empty. Write or generate the new one."
      : "Write or generate the methodology for this data."
    : hasExisting
    ? "This is the task's current methodology. Edit it freely — Generate adds a new section to the end."
    : hasExistingRows
    ? "The task has no methodology yet. Write or generate one."
    : "Write or generate the methodology for this data.";
  return (
    <Box bg="pureWhite" border="1px solid" borderColor="gray.200" borderRadius="14px" p="20px">
      <Flex justify="space-between" align="flex-start" gap={3} mb={1}>
        <HStack spacing={2}>
          <Flex w="26px" h="26px" borderRadius="7px" bg="lightLime" align="center" justify="center">
            <Compass size={14} color="var(--chakra-colors-limeShade-600)" />
          </Flex>
          <Text fontSize="16px" fontWeight="800" color="darkTeal">
            Methodology
          </Text>
          <Badge fontSize="10px" variant="outline" borderRadius="full" px={2} color="gray.500">
            Optional
          </Badge>
        </HStack>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Sparkle size={14} weight="fill" color="#85C22A" />}
          onClick={onGenerateDraft}
          isLoading={isGenerating}
          loadingText="Writing…"
          flexShrink={0}
        >
          Generate
        </Button>
      </Flex>
      <Text fontSize="13px" color="gray.500" mb={2}>
        What the task&apos;s methodology will be after this commit
      </Text>

      <Text fontSize="12px" color="gray.500" mb={2}>
        {note}
      </Text>

      {/* The generated text lands here, after whatever is already in the editor, rather than in a separate
          accept/discard card: the previous methodology and the new section are one statement, and the user
          has to be able to edit them together. The editor's own undo takes a generation back. */}
      <Box border="1px solid" borderColor="gray.100" borderRadius="10px" overflow="hidden">
        <RichTextEditor content={html} onChange={onChange} />
      </Box>
      <Text fontSize="11px" color="gray.400" mt={2}>
        Generated text is added to the editor — review and edit it before committing. Saved to the task&apos;s{" "}
        <Text as="span" fontWeight="700">Methodology</Text> field, the same one shown on the data task page. The
        full AI session trail is stored separately and can&apos;t be edited.
      </Text>
      {generateError && (
        <Alert status="error" borderRadius="md" mt={2} fontSize="12px">
          <AlertIcon />
          {generateError}
        </Alert>
      )}
    </Box>
  );
};

/**
 * The audit trail as it will be stored. Shown before the commit rather than after.
 */
const TrailPreview = ({ markdown, isOpen, onToggle }) => {
  if (!markdown) return null;
  return (
    <Box>
      <HStack as="button" type="button" onClick={onToggle} spacing={2} color="gray.600" _hover={{ color: "darkTeal" }}>
        <FileText size={15} weight="bold" />
        <Text fontSize="13px" fontWeight="600">
          Trail stored with this commit
        </Text>
        <Badge fontSize="10px" variant="subtle" bg="gray.100" color="gray.600" borderRadius="full" px={2}>
          {isOpen ? "Hide" : "Read"}
        </Badge>
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <Box
          mt={2}
          p={3}
          maxH="300px"
          overflowY="auto"
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          fontSize="12px"
          color="gray.700"
          sx={{
            "& h1, & h2, & h3": { fontWeight: "700", color: "darkTeal", mt: 3, mb: 1 },
            "& p": { mb: 2 },
            "& ul, & ol": { pl: 5, mb: 2 },
            "& code": { fontFamily: "mono", bg: "gray.200", px: 1, borderRadius: "sm" },
            "& table": { width: "100%", mb: 2 },
            "& th, & td": { border: "1px solid", borderColor: "gray.200", px: 2, py: 1, textAlign: "left" },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CommitPanel;
